import { Link } from "react-router-dom";
import { Lock, Clock, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { StatoAccount } from "@/lib/accesso";
import { useAuth } from "@/hooks/useAuth";
import { invitiAllAcquistoNellApp, TESTI_IOS } from "@/lib/iscrizione";

function dataLunga(d: string | null | undefined): string | null {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Il popup che appare quando la prova o l'abbonamento sono finiti.
 *
 * E' il momento commerciale dell'app: chi lo vede ha provato il prodotto per
 * sette giorni, quindi il tono e' un invito, non un muro. Si puo' chiudere -
 * lo storico e i calcoli salvati restano suoi, e trattenerlo in ostaggio
 * davanti a un popup non firma nessun contratto - ma la chat resta ferma
 * finche' Datacom non attiva l'account.
 *
 * DUE SITUAZIONI, DUE TESTI (07/09/2026). Fino a oggi un CLIENTE con
 * l'abbonamento scaduto leggeva «il periodo di prova e' terminato… richiedi
 * l'account installatore»: a uno che ha gia' pagato, parole sbagliate e un
 * pulsante che sembra farlo ricominciare da capo. A lui si dice quando e'
 * scaduto, che i suoi marchi restano quelli, e che basta chiedere il rinnovo.
 */
export function BloccoProvaScaduta({
  aperto,
  onChiudi,
  stato,
}: {
  aperto: boolean;
  onChiudi: () => void;
  stato?: StatoAccount | null;
}) {
  const rinnovo = stato?.abbonamentoScaduto ?? null;
  const quando = dataLunga(rinnovo?.il);
  // Sull'iPhone niente richiesta di attivazione ne' contatti per comprare
  // (Apple 3.1.1, 29/09): si dice come stanno le cose, e basta.
  const conRichiesta = invitiAllAcquistoNellApp();

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
            {rinnovo ? (
              <RefreshCw className="h-6 w-6 text-primary" />
            ) : (
              <Lock className="h-6 w-6 text-primary" />
            )}
          </div>
          <DialogTitle className="text-center">
            {rinnovo ? "Il tuo accesso è scaduto" : "Il periodo di valutazione è terminato"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {!conRichiesta ? (
              <>
                {rinnovo ? TESTI_IOS.accessoNonAttivo : TESTI_IOS.valutazioneFinita} Le tue
                conversazioni e i calcoli salvati restano consultabili.
              </>
            ) : rinnovo ? (
              <>
                L&apos;accesso su <strong>{rinnovo.marchi.join(", ")}</strong>
                {quando ? <> è finito il <strong>{quando}</strong></> : <> è finito</>}.
                Contatta Datacom per la riattivazione: i tuoi marchi e le tue
                conversazioni restano dove li hai lasciati, e l&apos;assistente
                riparte appena Datacom conferma.
              </>
            ) : (
              <>
                I 7 giorni di valutazione sono finiti. Per continuare serve
                l&apos;<strong>account installatore</strong>, che Datacom
                Tecnologie attiva per i propri clienti: invia la richiesta e
                verrai sbloccato appena approvata.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {conRichiesta && (
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button asChild className="w-full">
            <Link to="/richiedi-accesso">
              {rinnovo ? "Contatta Datacom" : "Richiedi l'attivazione"}
            </Link>
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {rinnovo ? (
              <>
                Oppure scrivi a{" "}
                <a href="mailto:info@datacomtecnologie.it" className="underline underline-offset-2">
                  info@datacomtecnologie.it
                </a>
                . Le tue conversazioni e i calcoli salvati restano consultabili.
              </>
            ) : (
              <>Le tue conversazioni e i calcoli salvati restano tuoi e consultabili.</>
            )}
          </p>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Il banner degli ultimi giorni: avvisare PRIMA di bloccare.
 *
 * Un account che smette di funzionare di colpo genera una telefonata arrabbiata;
 * uno che avvisa per tre giorni genera una richiesta di sblocco. Compare solo
 * quando mancano pochi giorni: per tutto il resto della prova l'app si usa e
 * basta, senza conto alla rovescia addosso.
 */
export function BannerProva({ stato }: { stato: StatoAccount | null }) {
  const { isAdmin } = useAuth();
  // L'admin non ha una prova da consumare ne' un abbonamento che scade: in
  // riunione (27/08) il banner e' comparso durante la dimostrazione a Umberto,
  // sull'account che gestisce gli altri. Non e' solo brutto: e' una riga che
  // dice una cosa falsa a chi il prodotto lo vende.
  if (isAdmin) return null;
  if (!stato || stato.giorniRimanenti === undefined) return null;
  const giorni = stato.giorniRimanenti;
  if (stato.tipo === "prova" && giorni > 3) return null;
  if (stato.tipo === "attivo" && giorni > 7) return null;
  if (stato.tipo === "scaduto") return null;

  const testo =
    stato.tipo === "prova"
      ? giorni <= 1
        ? "Ultimo giorno di valutazione."
        : `${giorni} giorni di valutazione rimanenti.`
      : giorni <= 1
        ? "Il tuo accesso scade oggi."
        : `Il tuo accesso scade fra ${giorni} giorni.`;

  return (
    <div className="flex items-center justify-center gap-2 border-b border-primary/30 bg-primary/10 px-3 py-2 text-[13px]">
      <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span>{testo}</span>
      {invitiAllAcquistoNellApp() && (
        <Link to="/richiedi-accesso" className="font-medium underline underline-offset-2">
          {stato.tipo === "prova" ? "Richiedi l'attivazione" : "Contatta Datacom"}
        </Link>
      )}
    </div>
  );
}
